import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import { fetchWithAuth } from '../utils/auth';
import { refusalText } from './live-review/account-reasons';
import {
  listLiveReportedSessions,
  LiveReportedSessionSummary,
} from '../services/liveAccessApi';

interface ReportSummary {
  totalReports: number;
  ItemType: string;
  UserID?: string;
  ItemID?: string;
  ProductID?: string;
  ReviewID?: string;
  CommentID?: string;
  reporterCount: number;
  Action?: string | null;
  Reviewed?: string;
}

type VideoStatus = 'under_review' | 'uploaded' | 'failed' | 'review_failed';
type ReportView = 'content' | 'live';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [reportView, setReportView] = useState<ReportView>('content');

  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const [itemType, setItemType] = useState('');
  const [userId] = useState('');
  const [itemId] = useState('');
  const [commentId] = useState('');
  const [productId] = useState('');
  const [actionLoading, setActionLoading] = useState<{ id: string; action: 'DELETE' | 'BLOCK' | null }>({
    id: '',
    action: null,
  });
  const [action] = useState('');
  const [reviewed] = useState('');
  const [videoStatus, setVideoStatus] = useState<VideoStatus>('under_review');
  const [pushLoading, setPushLoading] = useState(false);
  const [searchVideoId, setSearchVideoId] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  const [liveReports, setLiveReports] = useState<LiveReportedSessionSummary[]>([]);
  const [liveLoading, setLiveLoading] = useState(false);

  const fetchReports = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return;
    }

    setLoading(true);
    try {
      const body: Record<string, any> = { From: 0, BatchSize: 50 };
      if (itemType) body.ItemType = itemType;
      if (userId) body.UserID = userId;
      if (itemId) body.ItemID = itemId;
      if (commentId) body.CommentID = commentId;
      if (productId) body.ProductID = productId;
      if (reviewed) body.Reviewed = reviewed;
      if (action) body.Action = action;

      const res = await fetch(`${getApiBaseUrl()}/video/t/findAllReports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to fetch reports');
      }

      setReports(json.data as ReportSummary[]);
    } catch (err: any) {
      toast.error(err.message || 'Network error, try again');
    } finally {
      setLoading(false);
    }
  }, [action, commentId, itemId, itemType, productId, reviewed, userId]);

  const fetchLiveReports = useCallback(async () => {
    setLiveLoading(true);
    try {
      const response = await listLiveReportedSessions({ page: 1, limit: 50 });
      setLiveReports(response.sessions);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load live reports');
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    if (reportView === 'content') {
      fetchReports();
      return;
    }

    fetchLiveReports();
  }, [fetchLiveReports, fetchReports, reportView]);

  const getId = (r: ReportSummary) => r.UserID || r.ItemID || r.ProductID || r.ReviewID || r.CommentID || '—';

  const handleAction = async (id: string, type: string, nextAction: 'DELETE' | 'BLOCK') => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return;
    }

    setActionLoading({ id, action: nextAction });
    try {
      const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/reportAction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ReportID: id,
          ItemType: type,
          Action: nextAction,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(refusalText(res.status, json.message, `${nextAction} failed`));
      }

      toast.success(json.message || `${nextAction} succeeded`);
      fetchReports();
    } catch (err: any) {
      toast.error(err.message || `Network error during ${nextAction.toLowerCase()}`);
    } finally {
      setActionLoading({ id: '', action: null });
    }
  };

  const handlePushVideosByStatus = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return;
    }

    setPushLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/video-interaction/t/pushVideosByStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ sVideoStatus: videoStatus }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to push videos');
      }

      toast.success(json.message || `Pushed videos with status ${videoStatus}`);
    } catch (err: any) {
      toast.error(err.message || 'Network error while pushing videos');
    } finally {
      setPushLoading(false);
    }
  };

  const handleFetchVideoById = async () => {
    if (!searchVideoId.trim()) {
      toast.error('Please enter a video ID');
      return;
    }

    setSearchLoading(true);
    try {
      navigate(`/report/${searchVideoId}/VIDEO`);
    } finally {
      setSearchLoading(false);
    }
  };

  const contentStats = useMemo(
    () => ({
      totalReports: reports.reduce((sum, report) => sum + report.totalReports, 0),
      totalItems: reports.length,
      pendingReview: reports.filter((report) => !report.Reviewed).length,
    }),
    [reports],
  );

  const liveStats = useMemo(
    () => ({
      totalReports: liveReports.reduce((sum, report) => sum + report.reportCount, 0),
      totalSessions: liveReports.length,
      totalHosts: new Set(liveReports.map((report) => report.hostUserId).filter(Boolean)).size,
    }),
    [liveReports],
  );

  const getItemTypeIcon = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return '🎥';
      case 'USER':
        return '👤';
      case 'COMMENT':
        return '💬';
      case 'PRODUCT':
        return '🛍️';
      case 'REVIEW':
        return '⭐';
      default:
        return '📄';
    }
  };

  const getItemTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'USER':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'COMMENT':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'PRODUCT':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'REVIEW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <>
      <Navbar />
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <div className='fixed inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl' />
          <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl' />
        </div>

        <div className='relative bg-white/5 backdrop-blur-sm border-b border-white/10'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
            <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
              <div>
                <h1 className='text-3xl font-bold text-white'>Reports Dashboard</h1>
                <p className='mt-1 text-sm text-gray-400'>
                  Review content reports and participant-submitted livestream reports.
                </p>
              </div>
              <div className='flex gap-3'>
                <button
                  onClick={() => setReportView('content')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                    reportView === 'content'
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                      : 'border border-white/15 text-gray-300 hover:bg-white/5'
                  }`}
                >
                  Content Reports
                </button>
                <button
                  onClick={() => setReportView('live')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                    reportView === 'live'
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                      : 'border border-white/15 text-gray-300 hover:bg-white/5'
                  }`}
                >
                  Live Reports
                </button>
                <button
                  onClick={() => navigate('/videos')}
                  className='rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-white/5'
                >
                  All Videos
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          {reportView === 'content' && (
            <>
              <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 mb-6 overflow-hidden'>
                <div className='bg-gradient-to-r from-[#FF9C15]/10 to-[#F42026]/10 px-6 py-4 border-b border-white/10'>
                  <h2 className='text-lg font-semibold text-white'>Quick Video Search</h2>
                </div>
                <div className='p-6'>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleFetchVideoById();
                    }}
                    className='flex flex-col sm:flex-row gap-4'
                  >
                    <input
                      type='text'
                      value={searchVideoId}
                      onChange={(event) => setSearchVideoId(event.target.value)}
                      placeholder='Enter video ID'
                      className='flex-1 rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-white placeholder-gray-500 focus:border-[#FF9C15] focus:outline-none'
                      spellCheck={false}
                    />
                    <button
                      type='submit'
                      disabled={searchLoading || !searchVideoId.trim()}
                      className='rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] px-6 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {searchLoading ? 'Loading...' : 'Fetch Video'}
                    </button>
                  </form>
                </div>
              </div>

              <div className='grid gap-6 lg:grid-cols-2 mb-6'>
                <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden'>
                  <div className='bg-gradient-to-r from-[#FF9C15]/10 to-[#F42026]/10 px-6 py-4 border-b border-white/10'>
                    <h2 className='text-lg font-semibold text-white'>Filters</h2>
                  </div>
                  <div className='p-6'>
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        fetchReports();
                      }}
                      className='flex flex-col gap-4 sm:flex-row'
                    >
                      <select
                        value={itemType}
                        onChange={(event) => setItemType(event.target.value)}
                        className='flex-1 rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-white focus:border-[#FF9C15] focus:outline-none'
                      >
                        <option value='' className='bg-slate-800'>All Types</option>
                        <option value='USER' className='bg-slate-800'>User</option>
                        <option value='VIDEO' className='bg-slate-800'>Video</option>
                        <option value='COMMENT' className='bg-slate-800'>Comment</option>
                        <option value='PRODUCT' className='bg-slate-800'>Product</option>
                        <option value='REVIEW' className='bg-slate-800'>Review</option>
                      </select>
                      <button
                        type='submit'
                        disabled={loading}
                        className='rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50'
                      >
                        {loading ? 'Loading...' : 'Apply Filters'}
                      </button>
                    </form>
                  </div>
                </div>

                <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden'>
                  <div className='bg-gradient-to-r from-[#FF9C15]/10 to-[#F42026]/10 px-6 py-4 border-b border-white/10'>
                    <h2 className='text-lg font-semibold text-white'>Video Queue Management</h2>
                  </div>
                  <div className='p-6 flex flex-col gap-4 sm:flex-row'>
                    <select
                      value={videoStatus}
                      onChange={(event) => setVideoStatus(event.target.value as VideoStatus)}
                      className='flex-1 rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-white focus:border-[#FF9C15] focus:outline-none'
                    >
                      <option value='under_review' className='bg-slate-800'>Under Review</option>
                      <option value='uploaded' className='bg-slate-800'>Uploaded</option>
                      <option value='failed' className='bg-slate-800'>Failed</option>
                      <option value='review_failed' className='bg-slate-800'>Review Failed</option>
                    </select>
                    <button
                      type='button'
                      onClick={handlePushVideosByStatus}
                      disabled={pushLoading}
                      className='rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50'
                    >
                      {pushLoading ? 'Processing...' : 'Push to Queue'}
                    </button>
                  </div>
                </div>
              </div>

              {!loading && reports.length > 0 && (
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                  <div className='rounded-xl border border-white/10 bg-white/5 p-6'>
                    <p className='text-sm font-medium text-gray-400'>Total Reports</p>
                    <p className='mt-2 text-3xl font-bold text-white'>{contentStats.totalReports}</p>
                  </div>
                  <div className='rounded-xl border border-white/10 bg-white/5 p-6'>
                    <p className='text-sm font-medium text-gray-400'>Items Reported</p>
                    <p className='mt-2 text-3xl font-bold text-white'>{contentStats.totalItems}</p>
                  </div>
                  <div className='rounded-xl border border-white/10 bg-white/5 p-6'>
                    <p className='text-sm font-medium text-gray-400'>Pending Review</p>
                    <p className='mt-2 text-3xl font-bold text-white'>{contentStats.pendingReview}</p>
                  </div>
                </div>
              )}

              <div className='rounded-xl border border-white/10 bg-white/5 overflow-hidden'>
                {loading ? (
                  <div className='flex items-center justify-center py-16'>
                    <div className='h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-[#FF9C15]' />
                  </div>
                ) : reports.length === 0 ? (
                  <div className='px-6 py-16 text-center'>
                    <p className='text-xl font-semibold text-white'>No content reports found</p>
                  </div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='min-w-full'>
                      <thead className='bg-white/5 border-b border-white/10'>
                        <tr>
                          <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-300'>Type</th>
                          <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-300'>Item ID</th>
                          <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-300'>Reports</th>
                          <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-300'>Reporters</th>
                          <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-300'>Status</th>
                          <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-300'>Action</th>
                          <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-300'>Review Tools</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-white/10'>
                        {reports.map((report, index) => {
                          const id = getId(report);
                          return (
                            <tr
                              key={`${id}-${index}`}
                              className='cursor-pointer hover:bg-white/5'
                              onClick={() => navigate(`/report/${id}/${report.ItemType}`)}
                            >
                              <td className='px-6 py-4'>
                                <div className='flex items-center gap-3'>
                                  <span className='text-xl'>{getItemTypeIcon(report.ItemType)}</span>
                                  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getItemTypeBadgeColor(report.ItemType)}`}>
                                    {report.ItemType}
                                  </span>
                                </div>
                              </td>
                              <td className='px-6 py-4 text-sm font-mono text-gray-300'>{id}</td>
                              <td className='px-6 py-4 text-center text-sm font-semibold text-red-400'>{report.totalReports}</td>
                              <td className='px-6 py-4 text-center text-sm font-semibold text-blue-400'>{report.reporterCount}</td>
                              <td className='px-6 py-4 text-center'>
                                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                  report.Reviewed ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
                                }`}>
                                  {report.Reviewed ? 'Reviewed' : 'Pending'}
                                </span>
                              </td>
                              <td className='px-6 py-4 text-center text-sm text-gray-300'>{report.Action || '—'}</td>
                              <td className='px-6 py-4' onClick={(event) => event.stopPropagation()}>
                                <div className='flex justify-center gap-2'>
                                  <button
                                    onClick={() => handleAction(id, report.ItemType, 'DELETE')}
                                    disabled={actionLoading.id === id && actionLoading.action === 'DELETE'}
                                    className='rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50'
                                  >
                                    {actionLoading.id === id && actionLoading.action === 'DELETE' ? '...' : 'Delete'}
                                  </button>
                                  <button
                                    onClick={() => handleAction(id, report.ItemType, 'BLOCK')}
                                    disabled={actionLoading.id === id && actionLoading.action === 'BLOCK'}
                                    className='rounded-lg bg-yellow-500 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50'
                                  >
                                    {actionLoading.id === id && actionLoading.action === 'BLOCK' ? '...' : 'Block'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {reportView === 'live' && (
            <>
              {!liveLoading && (
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                  <div className='rounded-xl border border-white/10 bg-white/5 p-6'>
                    <p className='text-sm font-medium text-gray-400'>Live Reports</p>
                    <p className='mt-2 text-3xl font-bold text-white'>{liveStats.totalReports}</p>
                  </div>
                  <div className='rounded-xl border border-white/10 bg-white/5 p-6'>
                    <p className='text-sm font-medium text-gray-400'>Reported Sessions</p>
                    <p className='mt-2 text-3xl font-bold text-white'>{liveStats.totalSessions}</p>
                  </div>
                  <div className='rounded-xl border border-white/10 bg-white/5 p-6'>
                    <p className='text-sm font-medium text-gray-400'>Hosts Reported</p>
                    <p className='mt-2 text-3xl font-bold text-white'>{liveStats.totalHosts}</p>
                  </div>
                </div>
              )}

              <div className='rounded-xl border border-white/10 bg-white/5 overflow-hidden'>
                {liveLoading ? (
                  <div className='flex items-center justify-center py-16'>
                    <div className='h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-[#FF9C15]' />
                  </div>
                ) : liveReports.length === 0 ? (
                  <div className='px-6 py-16 text-center'>
                    <p className='text-xl font-semibold text-white'>No live reports found</p>
                    <p className='mt-2 text-sm text-gray-400'>Participant-submitted host reports will appear here.</p>
                  </div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='min-w-full'>
                      <thead className='bg-white/5 border-b border-white/10'>
                        <tr>
                          <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-300'>Host</th>
                          <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-300'>Livestream</th>
                          <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-300'>Reports</th>
                          <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-300'>Reporters</th>
                          <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-300'>Last Reported</th>
                          <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-300'>Status</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-white/10'>
                        {liveReports.map((report) => (
                          <tr
                            key={report.sessionId}
                            className='cursor-pointer hover:bg-white/5'
                            onClick={() =>
                              navigate(`/live-access/${encodeURIComponent(report.hostUserId)}/livestreams/${encodeURIComponent(report.sessionId)}`)
                            }
                          >
                            <td className='px-6 py-4'>
                              <div className='flex items-center gap-3'>
                                {report.host?.ProfileImageURL ? (
                                  <img src={report.host.ProfileImageURL} alt={report.host.Name || report.host.UserName || report.hostUserId} className='h-10 w-10 rounded-full object-cover' />
                                ) : (
                                  <div className='flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white'>
                                    {(report.host?.Name || report.host?.UserName || report.hostUserId || '?').charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div className='min-w-0'>
                                  <p className='truncate text-sm font-semibold text-white'>
                                    {report.host?.Name || report.host?.UserName || report.hostUserId}
                                  </p>
                                  <p className='truncate text-xs font-mono text-gray-400'>{report.hostUserId}</p>
                                </div>
                              </div>
                            </td>
                            <td className='px-6 py-4'>
                              <div className='flex items-center gap-3'>
                                {report.coverUrl ? (
                                  <img src={report.coverUrl} alt={report.sessionTitle || report.sessionId} className='h-12 w-12 rounded-lg object-cover' />
                                ) : (
                                  <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-white/10 text-xs font-bold text-white'>
                                    LIVE
                                  </div>
                                )}
                                <div className='min-w-0'>
                                  <p className='truncate text-sm font-semibold text-white'>{report.sessionTitle || 'Untitled Livestream'}</p>
                                  <p className='truncate text-xs font-mono text-gray-400'>{report.sessionId}</p>
                                </div>
                              </div>
                            </td>
                            <td className='px-6 py-4 text-center text-sm font-semibold text-rose-300'>{report.reportCount}</td>
                            <td className='px-6 py-4 text-center text-sm font-semibold text-blue-300'>{report.reporterCount}</td>
                            <td className='px-6 py-4 text-sm text-gray-300'>{formatDate(report.lastReportedAt)}</td>
                            <td className='px-6 py-4'>
                              <span className='inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-gray-200'>
                                {report.sessionLifecycleStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Reports;
